import { useState, useRef, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { TABLET_VIEWPORT } from '../../../../guided-toolbox/utils/breakpoints'
import { AdviceSummaryArcs, AdviceSummaryRow } from '../../../../components/AdviceSummary'
import { numberToDollars, roundPercent } from '../../../../utils/utils'
import SVGWrapper from '../../../../components/SVGWrapper'
import { LoadingOverlay, ErrorPage } from '../../../../components'
import { XSmallSecondaryButton } from '../../../../components/Button'
import { Target, IconEdit } from '../../../../assets/icons'
import IncomeReplacementDrawer from '../../../goals/IncomeReplacement/IncomeReplacementDrawer'
import EditDrawer from '../../../overall-strategy/make-changes/EditDrawer'
import { StepContent } from '../../elements'
import { buildSummaryViewModel } from './summaryViewModel'
import MobileSection from './MobileSection'
import { createSummaryFormatters } from './summaryFormat'
import {
  Heading,
  Sections,
  Section,
  SectionTitle,
  GoalCard,
  GoalCardMain,
  GoalCardText,
  GoalCardLabel,
  GoalCardValue,
  GoalCardAmount,
  GoalCardEditIcon,
  WalkMeThroughButton,
  SUMMARY_COMPACT_VIEWPORT,
} from './style'

function Summary({ store, onWalkMeThrough }) {
  const theme = useTheme()
  const isCompact = useMediaQuery(SUMMARY_COMPACT_VIEWPORT)
  // Advice removes this action from the top nav at tablet widths and below.
  const showWalkMeThroughInSummary = useMediaQuery(TABLET_VIEWPORT)
  const [editGoalOpen, setEditGoalOpen] = useState(false)
  const [recalcStatus, setRecalcStatus] = useState('idle')

  // The section the make-changes drawer should open to; null closes it.
  const [editSection, setEditSection] = useState(null)
  const [prereqsReady, setPrereqsReady] = useState(false)

  // Prefetch data required by the make-changes drawer but not loaded by this flow.
  const prereqs = useRef(null)
  useEffect(() => {
    const load = (async () => {
      await Promise.all([
        store.plan ? null : store.getPlanOverview(),
        store.spouse ? null : store.getSpouse(),
      ])
      await Promise.all([store.getPrimaryEmployerConfig(), store.getSpouseEmployerConfig()])
    })()
    load
      .then(() => setPrereqsReady(true))
      .catch(err => console.error('Failed to load the make-changes data:', err))
    prereqs.current = load
  }, [store])

  const openMakeChanges = section => async () => {
    try {
      await prereqs.current
      setEditSection(section)
    } catch {
      // Prerequisite loading already logged the error; leave the drawer closed.
    }
  }

  const summary = buildSummaryViewModel(store)
  if (!summary) {
    return null
  }

  if (recalcStatus === 'error') {
    return <ErrorPage />
  }

  const { retirementAge, retirementSavings, additionalSavings, goals, companyStock, risk } = summary

  const runRecalculation = async (recompute, errorMessage) => {
    setRecalcStatus('loading')
    try {
      await recompute()
      setRecalcStatus('idle')
    } catch (err) {
      console.error(errorMessage, err)
      setRecalcStatus('error')
    }
  }

  // Save the goal, clear the modified case, and rebuild both recommendation cases.
  const saveGoal = goalPct => {
    // Close the drawer before rebuilding so the loading state appears immediately.
    setEditGoalOpen(false)
    return runRecalculation(async () => {
      await store.updatePerson({ id: store.person.id, retirementIncomeGoalPct: goalPct })
      store.resetModifiedCase()
      await Promise.all([store.getBaseCase(), store.getRecommendedCase()])
      await store.refreshAllBreakdown()
    }, 'Failed to save the income-replacement goal:')
  }

  // Run drawer recalculations here so Summary controls the loading and error states.
  const recalculateFromDrawer = runRecalculate => {
    setEditSection(null)
    return runRecalculation(runRecalculate, 'Failed to recalculate from the make-changes drawer:')
  }

  const { savingsValue, additionalValue, goalsValue, companyStockValue, riskValue } =
    createSummaryFormatters(isCompact, theme)

  // Build card data for mobile; desktop renders the rows directly.
  const mobileSections = isCompact
    ? [
        {
          key: 'retirementAge',
          title: 'Retirement age',
          onEdit: openMakeChanges('retirementAge'),
          editAriaLabel: 'Edit retirement age',
          rows: retirementAge.includeSpouse
            ? [
                {
                  key: 'participant',
                  subLabel: retirementAge.participant.name,
                  current: `${retirementAge.participant.current}`,
                  modified: `${retirementAge.participant.plan}`,
                  modifiedIsRegularWeight:
                    retirementAge.participant.plan === retirementAge.participant.current,
                },
                {
                  key: 'spouse',
                  subLabel: retirementAge.spouse.name,
                  current: `${retirementAge.spouse.current}`,
                  modified: `${retirementAge.spouse.plan}`,
                  modifiedIsRegularWeight:
                    retirementAge.spouse.plan === retirementAge.spouse.current,
                },
              ]
            : [
                {
                  key: 'participant',
                  current: `${retirementAge.participant.current}`,
                  modified: `${retirementAge.participant.plan}`,
                  modifiedIsRegularWeight:
                    retirementAge.participant.plan === retirementAge.participant.current,
                },
              ],
        },
        {
          key: 'retirementSavings',
          title: 'Retirement savings',
          onEdit: openMakeChanges('contribution'),
          editAriaLabel: 'Edit retirement savings',
          rows: [
            ...retirementSavings.map(plan => ({
              key: plan.id,
              subLabel: plan.ownerLabel,
              current: savingsValue({
                monthly: plan.currentMonthly,
                percent: plan.currentPercent,
                isPercentOfPay: plan.isPercentOfPay,
              }),
              modified: savingsValue({
                monthly: plan.recommendedMonthly,
                percent: plan.recommendedPercent,
                isPercentOfPay: plan.isPercentOfPay,
              }),
            })),
            ...(additionalSavings.visible
              ? [
                  {
                    key: 'additionalSavings',
                    subLabel: 'Additional savings',
                    current: additionalValue(additionalSavings.currentMonthly),
                    modified: additionalValue(additionalSavings.recommendedMonthly),
                  },
                ]
              : []),
          ],
        },
        ...(goals.visible
          ? [
              {
                key: 'goals',
                title: 'Goals',
                onEdit: openMakeChanges('goals'),
                editAriaLabel: 'Edit goals',
                rows: [
                  {
                    key: 'goals',
                    current: goalsValue(goals.current),
                    modified: goalsValue(goals.plan),
                  },
                ],
              },
            ]
          : []),
        ...(companyStock
          ? [
              {
                key: 'companyStock',
                title: 'Company Stock',
                onEdit: openMakeChanges('companyStock'),
                editAriaLabel: 'Edit company stock',
                rows: [
                  {
                    key: 'companyStock',
                    subLabel: companyStock.accountLabel,
                    current: companyStockValue(companyStock.current),
                    modified: companyStockValue(companyStock.recommended),
                  },
                ],
              },
            ]
          : []),
        {
          key: 'risk',
          title: 'Risk - relative to our preferred range',
          onEdit: openMakeChanges('risk'),
          editAriaLabel: 'Edit risk',
          rows: [
            {
              key: 'risk',
              current: riskValue(risk.current),
              modified: riskValue(risk.plan),
              modifiedIsRegularWeight: true,
            },
          ],
        },
      ]
    : []

  return (
    <StepContent>
      <Heading>Review your plan and make changes if needed.</Heading>

      <GoalCard
        as={isCompact ? 'button' : 'div'}
        aria-label={isCompact ? 'Edit goal' : undefined}
        {...(isCompact ? { type: 'button', onClick: () => setEditGoalOpen(true) } : {})}>
        <GoalCardMain>
          <SVGWrapper svg={Target} size='custom30' />
          <GoalCardText>
            <GoalCardLabel>Target retirement income</GoalCardLabel>
            <GoalCardValue>
              $
              <GoalCardAmount>{numberToDollars(summary.goalCard.amount ?? 0, true)}</GoalCardAmount>
              /mo | {roundPercent(summary.goalCard.percent)}%
            </GoalCardValue>
          </GoalCardText>
        </GoalCardMain>

        {isCompact ? (
          <GoalCardEditIcon aria-hidden='true'>
            <SVGWrapper svg={IconEdit} size='mediumLarge' />
          </GoalCardEditIcon>
        ) : (
          <XSmallSecondaryButton type='button' onClick={() => setEditGoalOpen(true)}>
            Edit goal
          </XSmallSecondaryButton>
        )}
      </GoalCard>

      <AdviceSummaryArcs
        current={summary.incomeChart.current}
        recommended={summary.incomeChart.recommended}
        modified={summary.incomeChart.modified}
        goalPercent={summary.incomeChart.goalPercent}
      />

      <Sections>
        {isCompact ? (
          <>
            {mobileSections.map(({ key, ...section }) => (
              <MobileSection key={key} {...section} />
            ))}

            {/* Disabled without a handler, matching SkipToSummaryLink — that is
                how the flow locks this path while a response streams. */}
            <WalkMeThroughButton
              type='button'
              onClick={onWalkMeThrough}
              disabled={!onWalkMeThrough}>
              Walk me through step-by-step
            </WalkMeThroughButton>
          </>
        ) : (
          <>
            <Section>
              {retirementAge.includeSpouse ? (
                <>
                  <SectionTitle>Retirement age</SectionTitle>

                  <AdviceSummaryRow
                    label={retirementAge.participant.name}
                    labelPlacement='gutter'
                    current={`${retirementAge.participant.current}`}
                    modified={`${retirementAge.participant.plan}`}
                    onEdit={openMakeChanges('retirementAge')}
                    editAriaLabel='Edit retirement age'
                  />

                  <AdviceSummaryRow
                    label={retirementAge.spouse.name}
                    labelPlacement='gutter'
                    current={`${retirementAge.spouse.current}`}
                    modified={`${retirementAge.spouse.plan}`}
                    onEdit={openMakeChanges('retirementAge')}
                    editAriaLabel='Edit retirement age'
                  />
                </>
              ) : (
                <AdviceSummaryRow
                  label='Retirement age'
                  current={`${retirementAge.participant.current}`}
                  modified={`${retirementAge.participant.plan}`}
                  onEdit={openMakeChanges('retirementAge')}
                />
              )}
            </Section>

            <Section>
              <SectionTitle>Retirement savings</SectionTitle>

              {retirementSavings.map(plan => (
                <AdviceSummaryRow
                  key={plan.id}
                  label={plan.ownerLabel}
                  labelPlacement='gutter'
                  current={savingsValue({
                    monthly: plan.currentMonthly,
                    percent: plan.currentPercent,
                    isPercentOfPay: plan.isPercentOfPay,
                  })}
                  modified={savingsValue(
                    {
                      monthly: plan.recommendedMonthly,
                      percent: plan.recommendedPercent,
                      isPercentOfPay: plan.isPercentOfPay,
                    },
                    true
                  )}
                  onEdit={openMakeChanges('contribution')}
                  editAriaLabel={`Edit ${plan.ownerLabel}`}
                />
              ))}

              {additionalSavings.visible && (
                <AdviceSummaryRow
                  label='Additional savings'
                  labelPlacement='gutter'
                  current={additionalValue(additionalSavings.currentMonthly)}
                  modified={additionalValue(additionalSavings.recommendedMonthly, true)}
                  onEdit={openMakeChanges('additionalSavings')}
                  editAriaLabel='Edit additional savings'
                />
              )}
            </Section>

            {goals.visible && (
              <Section>
                <AdviceSummaryRow
                  label='Goals'
                  current={goalsValue(goals.current)}
                  modified={goalsValue(goals.plan, true)}
                  onEdit={openMakeChanges('goals')}
                />
              </Section>
            )}

            {companyStock && (
              <Section>
                <SectionTitle>Company Stock</SectionTitle>

                <AdviceSummaryRow
                  label={companyStock.accountLabel}
                  labelPlacement='gutter'
                  current={companyStockValue(companyStock.current)}
                  modified={companyStockValue(companyStock.recommended, true)}
                  onEdit={openMakeChanges('companyStock')}
                  editAriaLabel='Edit company stock'
                />
              </Section>
            )}

            <Section>
              <AdviceSummaryRow
                label='Risk - relative to our preferred range'
                current={riskValue(risk.current)}
                modified={riskValue(risk.plan, true)}
                onEdit={openMakeChanges('risk')}
                editAriaLabel='Edit risk'
              />
            </Section>

            {showWalkMeThroughInSummary && (
              <WalkMeThroughButton
                type='button'
                onClick={onWalkMeThrough}
                disabled={!onWalkMeThrough}>
                Walk me through step-by-step
              </WalkMeThroughButton>
            )}
          </>
        )}
      </Sections>

      <IncomeReplacementDrawer
        active={editGoalOpen}
        toggleDrawer={() => setEditGoalOpen(false)}
        handleSave={saveGoal}
      />

      {/* Keep the drawer mounted after prerequisites load so `active` controls its animation. */}
      {prereqsReady && (
        <EditDrawer
          active={!!editSection}
          scrollToSection={editSection}
          handleCancel={() => setEditSection(null)}
          onCaseChange={() => store.refreshAllBreakdown()}
          onRecalculate={recalculateFromDrawer}
        />
      )}

      {recalcStatus === 'loading' && <LoadingOverlay />}
    </StepContent>
  )
}

Summary.propTypes = {
  /** Restarts the step-by-step flow from the first step. */
  onWalkMeThrough: PropTypes.func,
}

export default inject('store')(observer(Summary))
